import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmployeeOverview } from '../services/EmployeeService';
import { FaBriefcase, FaCalendarAlt, FaClock, FaUmbrellaBeach, FaClipboardList, FaArrowRight, FaArrowUp, FaArrowDown, FaEuroSign } from 'react-icons/fa';
import { normalizeOverview, formatNum } from '../utils/normalizeOverview';
import { formatDaysHalf } from '../utils/leaveFormat';
import { statutoryDaysPerYearFromPattern } from '../utils/leaveStatutory';
import { monthsEmployedInYear } from '../utils/leaveCalc';
import TimeTrackingService, { TimeTracking } from '../../timetracking/services/TimeTrackingService';
import httpService from '../../../core/services/HttpService';
import { useUserRole } from '../../../hooks/useUserRole';
import { personalakteStyles as ps, colors } from '../styles/personalakteStyles';

interface EmployeeOverviewTabProps {
  employee: EmployeeOverview;
  employeeId: number;
  profile?: any;
  onTabChange?: (tab: any) => void;
}

const EmployeeOverviewTab: React.FC<EmployeeOverviewTabProps> = ({ employee, employeeId, profile, onTabChange }) => {
  const navigate = useNavigate();
  const { isAdmin, permissions: userPermissions } = useUserRole();
  const canViewDocuments = isAdmin || userPermissions.can_view_documents;
  const ov = normalizeOverview(employee);

  const [timeTrackings, setTimeTrackings] = useState<TimeTracking[]>([]);
  const [loadingTT, setLoadingTT] = useState(true);
  const [allAbsences, setAllAbsences] = useState<any[]>([]);

useEffect(() => {
    const loadTimeTrackings = async () => {
      try {
        setLoadingTT(true);

        const isDeactivated = ov.onboarding_status === 'DEACTIVATED';
        const referenceDate = isDeactivated && ov.deactivated_at
          ? new Date(ov.deactivated_at)
          : new Date();
        const lookbackStart = new Date(referenceDate);
        lookbackStart.setDate(lookbackStart.getDate() - 30);

        const tts = await TimeTrackingService.fetchTimeTrackings({
          employee: employeeId,
          start_date: lookbackStart.toISOString().split('T')[0],
          end_date: referenceDate.toISOString().split('T')[0],
        });

        const sorted = tts.sort((a, b) => {
          const dateA = new Date(a.start_time).getTime();
          const dateB = new Date(b.start_time).getTime();
          return dateB - dateA;
        });

        setTimeTrackings(sorted);
      } catch (error: any) {
        console.error('[EmployeeOverview] Failed to load time trackings:', error);
        setTimeTrackings([]);
      } finally {
        setLoadingTT(false);
      }
    };

    if (employeeId && employeeId > 0) {
      loadTimeTrackings();
    } else {
      setLoadingTT(false);
    }
  }, [employeeId]);

  useEffect(() => {
    if (!employeeId || employeeId <= 0) return;
    let cancelled = false;
    const currentYear = new Date().getFullYear();
    httpService.get<any>(`/api/absences/?employee=${employeeId}&from=${currentYear}-01-01&to=${currentYear}-12-31`)
      .then(res => {
        if (cancelled) return;
        const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
        setAllAbsences(data);
      })
      .catch(() => {
        if (cancelled) return;
        setAllAbsences([]);
      });
    return () => { cancelled = true; };
  }, [employeeId]);

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatDateWithWeekday = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const weekday = date.toLocaleDateString('de-DE', { weekday: 'short' });
    const dateStr = date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
    return `${weekday}, ${dateStr}`;
  };

  const formatTime = (timeString?: string): string => {
    if (!timeString) return '--:--';
    if (timeString.includes('T')) {
      const date = new Date(timeString);
      return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    }
    return timeString.substring(0, 5);
  };

  const days = profile?.working_pattern?.days_distribution || [];
  const statutoryBE = profile?.leave?.statutory_days_per_year ?? null;
  const statutoryUI = statutoryBE ?? statutoryDaysPerYearFromPattern(days);
  const extra = profile?.leave?.holiday_extra_days_per_year ?? 0;
  const totalContractual = statutoryUI != null ? statutoryUI + Number(extra) : 0;

  const startDate = profile?.job?.start_date ?? null;
  const endDate = profile?.job?.contract_end_date ?? null;
  const currentYear = new Date().getFullYear();
  const months = monthsEmployedInYear(startDate, endDate, currentYear);

  const entitlementThisYear = (totalContractual * months) / 12;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const getDays = (a: any) => {
    if (a.days) return a.days;
    const startRaw = a.start_datetime || a.start_date;
    const endRaw = a.end_datetime || a.end_date;
    if (a.full_day !== false) {
      const s = new Date(startRaw.split('T')[0]);
      const e = new Date(endRaw.split('T')[0]);
      return Math.max(1, Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)) + 1);
    }
    return 1;
  };

  const upcomingApproved = (ov.upcoming_absences || []).filter((a: any) => a.status === 'APPROVED');
  const approvedFutureDays = upcomingApproved
    .filter((a: any) => a.absence_type === 'HOLIDAY')
    .reduce((sum: number, a: any) => sum + (a.days || getDays(a)), 0);

  const pastAbsences = allAbsences
    .filter((a: any) => new Date(a.end_datetime || a.end_date) < today)
    .sort((a: any, b: any) => new Date(b.start_datetime || b.start_date).getTime() - new Date(a.start_datetime || a.start_date).getTime());

  const usedDays = pastAbsences
    .filter((a: any) => a.status === 'APPROVED' && a.absence_type === 'HOLIDAY')
    .reduce((sum: number, a: any) => sum + getDays(a), 0);

  const totalPlanned = usedDays + approvedFutureDays;
  const availableThisYear = Math.max(0, entitlementThisYear - totalPlanned);

  const leavePercentage = entitlementThisYear > 0
    ? Math.min(100, Math.max(0, (totalPlanned / entitlementThisYear) * 100))
    : 0;

  const employmentTypeLabels: Record<string, string> = {
    TRAINEE: 'Trainee',
    Trainee: 'Trainee',
    KURZFRISTIG: 'Kurzfristig beschäftigt',
    kurzfristig: 'Kurzfristig beschäftigt',
    MINIJOB: 'Minijob',
    Minijob: 'Minijob',
  };

  const employmentTypeDisplay = ov.key_facts.employment_type
    ? employmentTypeLabels[ov.key_facts.employment_type] || ov.key_facts.employment_type
    : '—';

  return (
    <div>
      {ov.key_facts.employment_type && (
        <div style={{
          marginBottom: 10,
          ...ps.innerContainer,
        }}>
          <span style={{ fontSize: 13, color: colors.textMuted, fontWeight: 500 }}>Beschäftigungsart:</span>
          {' '}
          <span style={{ fontSize: 14, fontWeight: 600, color: colors.navy }}>{employmentTypeDisplay}</span>
        </div>
      )}

      {(ov.balances?.month_current_h !== undefined || ov.balances?.month_prev_h !== undefined || ov.balances?.total_balance_h !== undefined) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 10, marginBottom: 10 }}>
          <div className="hover-card" style={ps.cardCompact}>
            <div style={ps.kpiLabel}>This Month</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <div style={ps.kpiValue}>
                {formatNum(ov.balances?.month_current_h ?? 0, 1)}h
              </div>
              {ov.balances?.delta_prev_h !== undefined && ov.balances?.delta_prev_h !== 0 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 13,
                  fontWeight: 600,
                  color: ov.balances.delta_prev_h > 0 ? colors.success : colors.danger
                }}>
                  {ov.balances.delta_prev_h > 0 ? <FaArrowUp size={11} /> : <FaArrowDown size={11} />}
                  {formatNum(Math.abs(ov.balances.delta_prev_h), 1)}h
                </div>
              )}
            </div>
            {ov.balances?.delta_prev_h !== undefined && ov.balances?.delta_prev_h !== 0 && (
              <div style={{ fontSize: 11, color: colors.textLight, marginTop: 4 }}>vs. prev</div>
            )}
          </div>

          <div className="hover-card" style={ps.cardCompact}>
            <div style={ps.kpiLabel}>Last Month</div>
            <div style={ps.kpiValue}>
              {formatNum(ov.balances?.month_prev_h ?? 0, 1)}h
            </div>
          </div>

          <div className="hover-card" style={ps.cardCompact}>
            <div style={ps.kpiLabel}>Total Balance</div>
            <div style={{
              ...ps.kpiValue,
              color: (ov.balances?.total_balance_h ?? 0) >= 0 ? colors.success : colors.danger
            }}>
              {formatNum(ov.balances?.total_balance_h ?? 0, 1)}h
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
        <div className="hover-card" style={ps.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <FaBriefcase size={16} color={colors.slate} />
            <h3 style={{ fontSize: 15, fontWeight: 600, color: colors.navy, margin: 0 }}>
              Key Skills
            </h3>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {ov.key_facts.employment_type && (
              <div>
                <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 4 }}>Employment Type</div>
                <div style={{ fontSize: 14, fontWeight: 500, color: colors.textBody }}>{ov.key_facts.employment_type}</div>
              </div>
            )}

            {ov.key_facts.weekly_hours > 0 && (
              <div>
                <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 4 }}>Weekly Hours</div>
                <div style={{ fontSize: 14, fontWeight: 500, color: colors.textBody }}>{formatNum(ov.key_facts.weekly_hours, 0)}h</div>
              </div>
            )}

            {ov.key_facts.contract_end_date && (
              <div>
                <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 4 }}>Contract End Date</div>
                <div style={{ fontSize: 14, fontWeight: 500, color: colors.textBody }}>
                  {formatDate(ov.key_facts.contract_end_date)}
                </div>
              </div>
            )}

            {!ov.key_facts.employment_type && ov.key_facts.weekly_hours === 0 && !ov.key_facts.contract_end_date && (
              <div style={{ fontSize: 13, color: colors.textLight, fontStyle: 'italic' }}>
                No key facts available
              </div>
            )}
          </div>
        </div>

        <div className="hover-card" style={{ ...ps.card, gridRow: 'span 2', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <FaUmbrellaBeach size={16} color={colors.slate} />
            <h3 style={{ fontSize: 15, fontWeight: 600, color: colors.navy, margin: 0 }}>
              Urlaub & Abwesenheiten
            </h3>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: colors.textMuted }}>Genommen / Geplant / Gesamt</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: colors.textBody }}>
                {formatDaysHalf(usedDays)} / {formatDaysHalf(approvedFutureDays)} / {formatDaysHalf(entitlementThisYear)} Tage
              </span>
            </div>

            <div
              style={{
                width: '100%',
                height: 6,
                background: colors.borderLight,
                borderRadius: 3,
                overflow: 'hidden',
                marginBottom: 6,
              }}
            >
              <div
                style={{
                  width: `${formatNum(leavePercentage, 0)}%`,
                  height: '100%',
                  background: leavePercentage < 70 ? colors.slate : leavePercentage < 90 ? colors.warning : colors.danger,
                  transition: 'width 0.3s ease',
                }}
              />
            </div>

            <div style={{ fontSize: 12, color: colors.textMuted }}>
              Verbleibend: {formatDaysHalf(availableThisYear)} Tage
            </div>
          </div>

          <div style={{ borderTop: `1px solid ${colors.borderLight}`, paddingTop: 16, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 12 }}>Upcoming Absences</div>
            {ov.upcoming_absences && ov.upcoming_absences.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {ov.upcoming_absences.slice(0, 3).map((absence: any) => {
                  const statusColors: Record<string, { bg: string; text: string }> = {
                    REQUESTED: { bg: colors.warningLight, text: colors.warning },
                    APPROVED: { bg: colors.successLight, text: colors.success },
                    REJECTED: { bg: colors.dangerLight, text: colors.danger },
                  };
                  const absenceColors = statusColors[absence.status] || { bg: colors.bgMuted, text: colors.textMuted };

                  return (
                    <div
                      key={absence.id}
                      className="hover-list-item"
                      style={ps.listItem}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: colors.textBody }}>
                          {formatDate(absence.start_date)}
                          {absence.start_date !== absence.end_date && ` - ${formatDate(absence.end_date)}`}
                        </span>
                        <span style={ps.badge(absenceColors.bg, absenceColors.text)}>
                          {absence.status}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 12, color: colors.textMuted }}>{absence.absence_type || 'Leave'}</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: colors.navy }}>
                          {formatDaysHalf(absence.days)} Tage
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: colors.textLight, fontStyle: 'italic', textAlign: 'center', padding: '8px 0' }}>
                No upcoming absences
              </div>
            )}

          </div>

          {/* Past Absences */}
          <div style={{ borderTop: `1px solid ${colors.borderLight}`, paddingTop: 16, display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 12 }}>Vergangene Abwesenheiten</div>
            {pastAbsences.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {pastAbsences.slice(0, 3).map((absence: any) => {
                  const startDate = absence.start_datetime?.split('T')[0] || absence.start_date;
                  const endDate = absence.end_datetime?.split('T')[0] || absence.end_date;
                  const statusColorsMap: Record<string, { bg: string; text: string }> = {
                    APPROVED: { bg: '#f1f5f9', text: colors.textMuted },
                    REJECTED: { bg: colors.dangerLight, text: colors.danger },
                    CANCELLED: { bg: colors.dangerLight, text: colors.danger },
                  };
                  const absenceColors = statusColorsMap[absence.status] || { bg: colors.bgMuted, text: colors.textMuted };

                  return (
                    <div key={absence.id} style={{ ...ps.listItem, opacity: 0.8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: colors.textMuted }}>
                          {formatDate(startDate)}
                          {startDate !== endDate && ` - ${formatDate(endDate)}`}
                        </span>
                        <span style={ps.badge(absenceColors.bg, absenceColors.text)}>
                          {absence.status}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 12, color: colors.textLight }}>{absence.absence_type || 'Leave'}</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: colors.textMuted }}>
                          {formatDaysHalf(getDays(absence))} Tage
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: colors.textLight, fontStyle: 'italic', textAlign: 'center', padding: '8px 0' }}>
                Keine vergangenen Abwesenheiten
              </div>
            )}

            <button
              className="hover-btn-secondary"
              onClick={() => navigate(`/shiftplan?tab=absences&emp=${employeeId}`)}
              style={{
                marginTop: 16,
                width: '100%',
                ...ps.actionButton,
              }}
            >
              Alle Abwesenheiten
              <FaArrowRight size={11} />
            </button>
          </div>
        </div>

        <div className="hover-card" style={ps.card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <FaCalendarAlt size={16} color={colors.slate} />
              <h3 style={{ fontSize: 15, fontWeight: 600, color: colors.navy, margin: 0 }}>
                Next Shifts
              </h3>
            </div>
            <button
              onClick={() => navigate(`/shiftplan?tab=shifts&emp=${employeeId}`)}
              style={{
                padding: '6px 12px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 500,
                color: colors.slate,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              View All
              <FaArrowRight size={10} />
            </button>
          </div>

          {ov.next_shifts.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {ov.next_shifts.slice(0, 4).map((shift) => {
                const shiftDate = shift.date || (shift.start_time ? shift.start_time.split('T')[0] : null);
                return (
                  <div
                    key={shift.id}
                    className="hover-list-item"
                    style={ps.listItem}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: colors.textBody }}>
                        {formatDateWithWeekday(shiftDate)}
                      </span>
                      <span style={{ fontSize: 13, color: colors.textMuted }}>
                        {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: colors.textMuted }}>{shift.work_area_name || shift.work_area || 'No work area'}</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: colors.textLight, fontStyle: 'italic', textAlign: 'center', padding: '8px 0' }}>
              No upcoming shifts
            </div>
          )}
        </div>

        {isAdmin && (
          <div className="hover-card" style={ps.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <FaBriefcase size={16} color={colors.slate} />
              <h3 style={{ fontSize: 15, fontWeight: 600, color: colors.navy, margin: 0 }}>
                Employment
              </h3>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 8 }}>Current Position</div>
                <div style={ps.innerContainer}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: colors.navy }}>
                    {employmentTypeDisplay}
                    {ov.key_facts.weekly_hours > 0 && (
                      <span style={{ fontWeight: 400, color: colors.textMuted }}> · {formatNum(ov.key_facts.weekly_hours, 0)} h / week</span>
                    )}
                    {employee.start_date && (
                      <span style={{ fontWeight: 400, color: colors.textMuted }}>
                        {' · '}
                        {new Date(employee.start_date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                        {' - '}
                        {ov.key_facts.contract_end_date
                          ? new Date(ov.key_facts.contract_end_date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })
                          : 'ongoing'
                        }
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {(employee as any).employment_history && (employee as any).employment_history.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 8 }}>Previous Periods</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {(employee as any).employment_history.slice(0, 3).map((period: any, idx: number) => (
                      <div key={idx} style={ps.innerContainer}>
                        <div style={{ fontWeight: 500, color: colors.textBody, fontSize: 13 }}>
                          {employmentTypeLabels[period.employment_type] || period.employment_type}
                        </div>
                        <div style={{ color: colors.textMuted, fontSize: 12, marginTop: 2 }}>
                          {formatDate(period.start_date)} - {formatDate(period.end_date)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                <button
                  className="hover-btn-secondary"
                  onClick={() => navigate(`/payrolls?employee=${employeeId}`)}
                  style={{
                    flex: 1,
                    ...ps.actionButton,
                  }}
                >
                  <FaEuroSign size={12} />
                  View Payrolls
                </button>
                {canViewDocuments && (
                  <button
                    className="hover-btn-secondary"
                    onClick={() => navigate(`/employees/${employeeId}?tab=documents`)}
                    style={{
                      flex: 1,
                      ...ps.actionButton,
                    }}
                  >
                    <FaClipboardList size={12} />
                    Documents
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="hover-card" style={ps.card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <FaClock size={16} color={colors.slate} />
              <h3 style={{ fontSize: 15, fontWeight: 600, color: colors.navy, margin: 0 }}>
                Last Time Trackings
              </h3>
            </div>
            <button
              onClick={() => {
                if (onTabChange) {
                  onTabChange('timeTracking');
                } else {
                  navigate(`/shiftplan?tab=timeTrackings&emp=${employeeId}`);
                }
              }}
              style={{
                padding: '6px 12px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 500,
                color: colors.slate,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              View All
              <FaArrowRight size={10} />
            </button>
          </div>

          {loadingTT ? (
            <div style={{ fontSize: 13, color: colors.textMuted, textAlign: 'center', padding: '8px 0' }}>
              Loading...
            </div>
          ) : timeTrackings.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {timeTrackings.slice(0, 4).map((tt) => {
                const startDate = new Date(tt.start_time);
                const endDate = new Date(tt.end_time);
                const hours = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);

                return (
                  <div
                    key={tt.id}
                    className="hover-list-item"
                    style={ps.listItem}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: colors.textBody }}>
                        {formatDateWithWeekday(tt.start_time.split('T')[0])}
                      </span>
                      <span style={{ fontSize: 13, color: colors.textMuted }}>
                        {formatTime(tt.start_time)} - {formatTime(tt.end_time)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 12, color: colors.textMuted }}>{tt.work_area__name || 'Unknown Area'}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: colors.navy }}>
                        {formatNum(hours, 2)}h
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: colors.textLight, fontStyle: 'italic', textAlign: 'center', padding: '8px 0' }}>
              No time trackings
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeOverviewTab;
